import {
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { usuarioService } from "../services/api";

const Perfil = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [nomeEscritorio, setNomeEscritorio] = useState("");

  const [formData, setFormData] = useState({
    nome: user?.nome || "",
    email: user?.email || "",
    senhaAtual: "",
    novaSenha: "",
    confirmarSenha: "",
    receber_email_tarefa_atencao: user?.receber_email_tarefa_atencao !== false,
    receber_email_tarefa_atraso: user?.receber_email_tarefa_atraso !== false,
  });

  // Buscar nome do escritório
  useEffect(() => {
    const buscarNomeEscritorio = async () => {
      if (user?.escritorio_id) {
        try {
          const response = await usuarioService.getAll();
          const usuarioComEscritorio = response.usuarios.find(
            (u) => u.id === user.id,
          );
          if (usuarioComEscritorio?.escritorio?.nome) {
            setNomeEscritorio(usuarioComEscritorio.escritorio.nome);
          }
        } catch (error) {
          console.error("Erro ao buscar nome do escritório:", error);
        }
      }
    };

    buscarNomeEscritorio();
  }, [user]);

  const handleInputChange = (campo, valor) => {
    setFormData((prev) => ({
      ...prev,
      [campo]: valor,
    }));
    // Limpar mensagens quando o usuário começar a digitar
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validarDadosPessoais = () => {
    if (!formData.nome.trim()) {
      setError("Nome é obrigatório");
      return false;
    }

    if (!formData.email.trim()) {
      setError("Email é obrigatório");
      return false;
    }

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Email inválido");
      return false;
    }

    return true;
  };

  const validarSenha = () => {
    if (!formData.senhaAtual.trim()) {
      setError("Senha atual é obrigatória");
      return false;
    }

    if (!formData.novaSenha.trim()) {
      setError("Nova senha é obrigatória");
      return false;
    }

    if (formData.novaSenha.length < 6) {
      setError("Nova senha deve ter pelo menos 6 caracteres");
      return false;
    }

    if (!formData.confirmarSenha.trim()) {
      setError("Confirmação de senha é obrigatória");
      return false;
    }

    if (formData.novaSenha !== formData.confirmarSenha) {
      setError("Nova senha e confirmação devem ser iguais");
      return false;
    }

    if (formData.senhaAtual === formData.novaSenha) {
      setError("A nova senha deve ser diferente da senha atual");
      return false;
    }

    return true;
  };

  const salvarDadosPessoais = async () => {
    if (loading || !validarDadosPessoais()) return; // Evita cliques duplos

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Atualizar dados pessoais (sem senha)
      await usuarioService.update(user.id, {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        receber_email_tarefa_atencao: !!formData.receber_email_tarefa_atencao,
        receber_email_tarefa_atraso: !!formData.receber_email_tarefa_atraso,
      });

      // Atualizar dados no contexto
      updateUser({
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        receber_email_tarefa_atencao: !!formData.receber_email_tarefa_atencao,
        receber_email_tarefa_atraso: !!formData.receber_email_tarefa_atraso,
      });

      setSuccess("Dados pessoais atualizados com sucesso!");
    } catch (err) {
      setError(err.message || "Erro ao atualizar dados pessoais");
    } finally {
      setLoading(false);
    }
  };

  const alterarSenha = async () => {
    if (loading || !validarSenha()) return; // Evita cliques duplos

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await usuarioService.alterarSenha({
        senhaAtual: formData.senhaAtual,
        novaSenha: formData.novaSenha,
        confirmarSenha: formData.confirmarSenha,
      });

      // Limpar campos de senha após sucesso
      setFormData((prev) => ({
        ...prev,
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: "",
      }));

      setSuccess("Senha alterada com sucesso!");
    } catch (err) {
      setError(err.message || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 600, color: "#333" }}
      >
        Meu Perfil
      </Typography>

      {/* Mensagens de feedback */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Dados Pessoais */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: "#1976d2" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Dados Pessoais
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.nome}
                onChange={(e) => handleInputChange("nome", e.target.value)}
                disabled={loading}
                required
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                disabled={loading}
                required
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  onClick={salvarDadosPessoais}
                  disabled={loading}
                  sx={{ backgroundColor: "#1976d2" }}
                >
                  {loading ? "Salvando..." : "Salvar Dados"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Alterar Senha */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <LockIcon sx={{ mr: 1, color: "#1976d2" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Alterar Senha
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                label="Senha Atual"
                type="password"
                value={formData.senhaAtual}
                onChange={(e) =>
                  handleInputChange("senhaAtual", e.target.value)
                }
                disabled={loading}
                required
              />

              <TextField
                fullWidth
                label="Nova Senha"
                type="password"
                value={formData.novaSenha}
                onChange={(e) => handleInputChange("novaSenha", e.target.value)}
                disabled={loading}
                helperText="Mínimo de 6 caracteres"
                required
              />

              <TextField
                fullWidth
                label="Confirmar Nova Senha"
                type="password"
                value={formData.confirmarSenha}
                onChange={(e) =>
                  handleInputChange("confirmarSenha", e.target.value)
                }
                disabled={loading}
                required
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <LockIcon />
                  }
                  onClick={alterarSenha}
                  disabled={loading}
                  sx={{ backgroundColor: "#1976d2" }}
                >
                  {loading ? "Alterando..." : "Alterar Senha"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Preferências de email */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <EmailIcon sx={{ mr: 1, color: "#1976d2" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Preferências de email
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.receber_email_tarefa_atencao}
                    onChange={(e) =>
                      handleInputChange(
                        "receber_email_tarefa_atencao",
                        e.target.checked,
                      )
                    }
                  />
                }
                label="Receber email quando a tarefa estiver em atenção"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.receber_email_tarefa_atraso}
                    onChange={(e) =>
                      handleInputChange(
                        "receber_email_tarefa_atraso",
                        e.target.checked,
                      )
                    }
                  />
                }
                label="Receber email quando a tarefa estiver em atraso"
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  onClick={salvarDadosPessoais}
                  disabled={loading}
                  sx={{ backgroundColor: "#1976d2" }}
                >
                  {loading ? "Salvando..." : "Salvar Preferências"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Informações do Sistema */}
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <EmailIcon sx={{ mr: 1, color: "#1976d2" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Informações do Sistema
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  ID do Usuário:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {user?.id}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Perfil:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {user?.role === "admin" ? "Administrador" : "Padrão"}
                </Typography>
              </Box>

              {user?.is_super_admin && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Super Admin:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#d32f2f" }}
                  >
                    Sim
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Escritório:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {nomeEscritorio || `${user?.escritorio_id} (carregando...)`}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Perfil;
